import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  Check,
  ChevronRight,
  ChevronDown,
  Table,
  Folder,
  FolderOpen,
  Layout,
  Calendar,
  Layers,
  Code,
  Terminal,
  Search,
  Server,
  ChevronsDown,
  ChevronsUp,
  Box
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { datasetApi } from '../../api/datasetApi';
import { udfApi } from '../../api/udfApi';

const TreeNode = ({ item, level = 0, onToggle, onSelect, children, isExpanded, icon: Icon, iconColor, isSelected, isDisabled }) => {
  const isLeaf = !children;
  const paddingLeft = level === 0 ? '0' : level === 1 ? '2rem' : level === 2 ? '4rem' : '6rem';

  return (
    <div className="select-none">
      <div
        className={cn(
          "flex items-center gap-2 py-1.5 px-3 rounded-lg transition-all cursor-pointer group border-b border-gray-50/50 dark:border-gray-800/30",
          isExpanded && level === 0 ? "bg-gray-50/20 dark:bg-gray-950/20 hover:bg-gray-100/30 dark:hover:bg-gray-900/30" : "hover:bg-gray-50/20 dark:hover:bg-gray-800/10",
          isSelected && "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800",
          isDisabled && "opacity-50 cursor-not-allowed"
        )}
        style={{ paddingLeft }}
        onClick={() => {
          if (isDisabled) return;
          if (isLeaf) {
            onSelect && onSelect(item);
          } else {
            onToggle();
          }
        }}
      >
        <div className="w-3 flex items-center justify-center shrink-0">
          {!isLeaf ? (
            isExpanded ? <ChevronDown size={level === 0 ? 12 : 11} className="text-gray-400" /> : <ChevronRight size={level === 0 ? 12 : 11} className="text-gray-400" />
          ) : null}
        </div>

        <div className={cn(
          "shrink-0 transition-colors",
          isSelected && isLeaf ? "text-emerald-600" : iconColor || "text-gray-400"
        )}>
          {isSelected && isLeaf ? (
            <Check size={11} strokeWidth={3} />
          ) : Icon ? (
            isLeaf ? (
              <Icon size={11} />
            ) : (
              // For non-leaf nodes, use Folder/FolderOpen by default, but use provided icon if it's not Folder
              Icon === Folder || Icon === Server ? (
                isExpanded ? (
                  Icon === Server ? (
                    <Server size={level === 0 ? 13 : 12} className={iconColor} />
                  ) : (
                    <FolderOpen size={level === 0 ? 13 : 12} className={iconColor} />
                  )
                ) : (
                  <Icon size={level === 0 ? 13 : 12} className={iconColor} />
                )
              ) : (
                isExpanded ? (
                  <FolderOpen size={level === 0 ? 13 : 12} className={iconColor} />
                ) : (
                  <Folder size={level === 0 ? 13 : 12} className={iconColor} />
                )
              )
            )
          ) : (
            <Box size={11} />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <span className={cn(
            "font-black uppercase tracking-wider truncate block transition-colors",
            level === 0 ? "text-[10px] text-gray-700 dark:text-gray-300" : "text-[11px] text-gray-900 dark:text-white"
          )}>
            {item.name || item.id}
          </span>
          {item.signature && (
            <div className="text-[8px] font-mono text-gray-400 dark:text-gray-500 truncate block mt-0.5 opacity-60">
              {item.signature}
            </div>
          )}
          {item.type && !item.signature && level > 0 && (
            <div className="text-[8px] font-mono text-gray-400 dark:text-gray-500 truncate block mt-0.5 opacity-60">
              {item.type}
            </div>
          )}
          {item.status && item.status !== 'ready' && (
            <span className={cn(
              "text-[7px] font-black uppercase tracking-widest px-1 py-0.5 rounded-full border shrink-0 inline-block mt-0.5",
              item.status === 'failed' ? "bg-red-50 text-red-500 border-red-100" :
                "bg-amber-50 text-amber-500 border-amber-100 animate-pulse"
            )}>
              {item.status === 'loading' ? 'Loading' :
                item.status === 'generating' ? 'Generating' :
                  item.status}
            </span>
          )}
        </div>
        {level > 0 && (
          <span className="text-[8px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest shrink-0">
            {level === 1 && item.type}
          </span>
        )}
      </div>

      {isExpanded && children}
    </div>
  );
};

const CreateWorkspaceWizard = ({ isOpen, onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    selectedSources: [],
    selectedUDFs: []
  });
  const [availableDatasets, setAvailableDatasets] = useState([]);
  const [availableUDFs, setAvailableUDFs] = useState([]);
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(false);
  const [isLoadingUDFs, setIsLoadingUDFs] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  // Reset state when wizard closes
  React.useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setFormData({ name: '', description: '', selectedSources: [], selectedUDFs: [] });
    } else {
      loadDatasets();
      loadUDFs();
    }
  }, [isOpen]);

  const loadDatasets = async (silent = false) => {
    if (!silent) setIsLoadingDatasets(true);
    try {
      const data = await datasetApi.getDatasets();
      setAvailableDatasets(data);
    } catch (err) {
      console.error('Failed to load datasets:', err);
    } finally {
      if (!silent) setIsLoadingDatasets(false);
    }
  };

  const loadUDFs = async () => {
    setIsLoadingUDFs(true);
    try {
      const data = await udfApi.listUDFs();
      setAvailableUDFs(data);
    } catch (err) {
      console.error('Failed to load UDFs:', err);
    } finally {
      setIsLoadingUDFs(false);
    }
  };

  // Background polling for status updates
  React.useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      loadDatasets(true);
    }, 3000);

    return () => clearInterval(interval);
  }, [isOpen]);

  // Organize datasets into tree structure
  const datasetsTree = useMemo(() => {
    const tree = {
      manteo: [],
      bigquery: {} // project -> dataset -> [tables]
    };

    availableDatasets.forEach(d => {
      if (d.type === 'upload' || d.type === 'parquet' || d.type === 'csv' || d.type === 'sample') {
        tree.manteo.push(d);
      } else if (d.type === 'bigquery') {
        const parts = d.id.split('.');
        if (parts.length === 3) {
          const [project, dataset, table] = parts;
          if (!tree.bigquery[project]) {
            tree.bigquery[project] = {};
          }
          if (!tree.bigquery[project][dataset]) {
            tree.bigquery[project][dataset] = [];
          }
          tree.bigquery[project][dataset].push({ ...d, tableName: table });
        } else if (parts.length === 2) {
          const [dataset, table] = parts;
          const project = 'Current Project';
          if (!tree.bigquery[project]) tree.bigquery[project] = {};
          if (!tree.bigquery[project][dataset]) tree.bigquery[project][dataset] = [];
          tree.bigquery[project][dataset].push({ ...d, tableName: table });
        } else {
          const fallbackProject = 'Direct Connections';
          const fallbackDataset = 'Generic';
          if (!tree.bigquery[fallbackProject]) tree.bigquery[fallbackProject] = {};
          if (!tree.bigquery[fallbackProject][fallbackDataset]) tree.bigquery[fallbackProject][fallbackDataset] = [];
          tree.bigquery[fallbackProject][fallbackDataset].push({ ...d, tableName: d.name || d.id });
        }
      }
    });

    return tree;
  }, [availableDatasets]);

  // Organize functions into tree structure
  const functionsTree = useMemo(() => {
    const tree = {
      bigquery: {}, // project -> dataset -> [functions]
      custom: []
    };

    availableUDFs.forEach(func => {
      if (func.source === 'bigquery' && func.routine_id) {
        const parts = func.routine_id.split('.');
        if (parts.length >= 3) {
          const project = parts[0];
          const dataset = parts[1];
          const functionName = parts.slice(2).join('.');

          if (!tree.bigquery[project]) {
            tree.bigquery[project] = {};
          }
          if (!tree.bigquery[project][dataset]) {
            tree.bigquery[project][dataset] = [];
          }
          tree.bigquery[project][dataset].push({
            ...func,
            project,
            dataset,
            functionName
          });
        }
      } else if (func.source === 'custom') {
        tree.custom.push(func);
      }
    });

    return tree;
  }, [availableUDFs]);

  // Filter datasets based on search
  const filteredDatasetsTree = useMemo(() => {
    return {
      manteo: datasetsTree.manteo.filter(item =>
        (item.name || item.id || "").toLowerCase().includes(searchQuery.toLowerCase())
      ),
      bigquery: Object.fromEntries(
        Object.entries(datasetsTree.bigquery).map(([projectId, datasets]) => {
          const filteredDatasets = Object.fromEntries(
            Object.entries(datasets).map(([datasetId, items]) => [
              datasetId,
              items.filter(item =>
                (item.tableName || item.name || item.id || "").toLowerCase().includes(searchQuery.toLowerCase())
              )
            ]).filter(([_, items]) => items.length > 0)
          );
          return [projectId, filteredDatasets];
        }).filter(([_, datasets]) => Object.keys(datasets).length > 0)
      )
    };
  }, [datasetsTree, searchQuery]);

  // Filter functions based on search
  const filteredFunctionsTree = useMemo(() => {
    return {
      bigquery: Object.fromEntries(
        Object.entries(functionsTree.bigquery).map(([projectId, datasets]) => {
          const filteredDatasets = Object.fromEntries(
            Object.entries(datasets).map(([datasetId, items]) => [
              datasetId,
              items.filter(item => {
                const q = searchQuery.toLowerCase();
                return (item.functionName || item.name || "").toLowerCase().includes(q) ||
                  (item.signature || "").toLowerCase().includes(q) ||
                  (item.description || "").toLowerCase().includes(q);
              })
            ]).filter(([_, items]) => items.length > 0)
          );
          return [projectId, filteredDatasets];
        }).filter(([_, datasets]) => Object.keys(datasets).length > 0)
      ),
      custom: functionsTree.custom.filter(item => {
        const q = searchQuery.toLowerCase();
        return (item.name || "").toLowerCase().includes(q) ||
          (item.signature || "").toLowerCase().includes(q) ||
          (item.description || "").toLowerCase().includes(q);
      })
    };
  }, [functionsTree, searchQuery]);

  // Expand all nodes by default when data loads
  useEffect(() => {
    if (step === 1 && availableDatasets.length > 0) {
      const allNodeIds = new Set();
      if (datasetsTree.manteo.length > 0) allNodeIds.add('manteo');
      if (Object.keys(datasetsTree.bigquery).length > 0) allNodeIds.add('bigquery');
      Object.keys(datasetsTree.bigquery).forEach(project => {
        allNodeIds.add(project);
        Object.keys(datasetsTree.bigquery[project]).forEach(dataset => {
          allNodeIds.add(dataset);
        });
      });
      setExpandedNodes(allNodeIds);
    } else if (step === 2 && availableUDFs.length > 0) {
      const allNodeIds = new Set();
      if (Object.keys(functionsTree.bigquery).length > 0) allNodeIds.add('bigquery');
      if (functionsTree.custom.length > 0) allNodeIds.add('custom');
      Object.keys(functionsTree.bigquery).forEach(project => {
        allNodeIds.add(project);
        Object.keys(functionsTree.bigquery[project]).forEach(dataset => {
          allNodeIds.add(dataset);
        });
      });
      setExpandedNodes(allNodeIds);
    }
  }, [step, availableDatasets, availableUDFs, datasetsTree, functionsTree]);

  if (!isOpen) return null;

  const handleNext = () => {
    setSearchQuery(''); // Reset search when moving to next step
    setStep(s => Math.min(s + 1, 3));
  };
  const handleBack = () => {
    setSearchQuery(''); // Reset search when going back
    setStep(s => Math.max(s - 1, 1));
  };

  const toggleNode = (nodeId) => {
    const next = new Set(expandedNodes);
    next.has(nodeId) ? next.delete(nodeId) : next.add(nodeId);
    setExpandedNodes(next);
  };

  const expandAll = () => {
    const allNodeIds = new Set();
    if (step === 1) {
      if (datasetsTree.manteo.length > 0) allNodeIds.add('manteo');
      if (Object.keys(datasetsTree.bigquery).length > 0) allNodeIds.add('bigquery');
      Object.keys(datasetsTree.bigquery).forEach(project => {
        allNodeIds.add(project);
        Object.keys(datasetsTree.bigquery[project]).forEach(dataset => {
          allNodeIds.add(dataset);
        });
      });
    } else {
      if (Object.keys(functionsTree.bigquery).length > 0) allNodeIds.add('bigquery');
      if (functionsTree.custom.length > 0) allNodeIds.add('custom');
      Object.keys(functionsTree.bigquery).forEach(project => {
        allNodeIds.add(project);
        Object.keys(functionsTree.bigquery[project]).forEach(dataset => {
          allNodeIds.add(dataset);
        });
      });
    }
    setExpandedNodes(allNodeIds);
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  const toggleUDF = (udf) => {
    setFormData(prev => {
      const exists = prev.selectedUDFs.find(u => u.id === udf.id);
      if (exists) {
        return { ...prev, selectedUDFs: prev.selectedUDFs.filter(u => u.id !== udf.id) };
      }
      return { ...prev, selectedUDFs: [...prev.selectedUDFs, udf] };
    });
  };

  const toggleSource = (source) => {
    setFormData(prev => {
      const exists = prev.selectedSources.find(s => s.id === source.id);
      if (exists) {
        return { ...prev, selectedSources: prev.selectedSources.filter(s => s.id !== source.id) };
      }

      // If we are adding a new source, check if it matches the connector type of already selected sources
      const currentSelectedType = prev.selectedSources.length > 0 ? (
        prev.selectedSources[0].type === 'bigquery' ? 'bigquery' : 'local'
      ) : null;

      const newSourceType = source.type === 'bigquery' ? 'bigquery' : 'local';

      if (currentSelectedType && currentSelectedType !== newSourceType) {
        // Enforce single source type
        alert(`Currently, workspaces can only contain datasets of the same type. This workspace is already configured for ${currentSelectedType} sources.`);
        return prev;
      }

      return { ...prev, selectedSources: [...prev.selectedSources, source] };
    });
  };

  const handleCreate = () => {
    onCreated({
      name: formData.name,
      description: formData.description,
      sources: formData.selectedSources.map(s => s.id),
      udfs: formData.selectedUDFs.map(u => u.id)
    });
    onClose();
    // Reset wizard
    setStep(1);
    setFormData({ name: '', description: '', selectedSources: [], selectedUDFs: [] });
  };

  const ProgressIndicator = () => (
    <div className="flex gap-1.5 mb-8">
      {[1, 2, 3].map((num) => (
        <div
          key={num}
          className={cn(
            "h-1 rounded-full flex-1 transition-all duration-500",
            step >= num
              ? "bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.2)]"
              : "bg-gray-100 dark:bg-gray-800"
          )}
        />
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-950 w-full max-w-2xl rounded-[40px] shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="p-8 pb-0 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tighter">
              {step === 1 && "Connect Data Sources"}
              {step === 2 && "Select Functions (Optional)"}
              {step === 3 && "Final Review"}
            </h3>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
              {step === 1 && "Select datasets to include in this workspace"}
              {step === 2 && "Choose custom functions for your SQL agent"}
              {step === 3 && "Add workspace details and verify settings before initialization"}
            </p>
          </div>
          <button onClick={onClose} className="p-3 bg-gray-50 dark:bg-gray-900 text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-2xl transition-all">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 flex-1 overflow-y-auto custom-scrollbar">
          <ProgressIndicator />

          {step === 1 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
              {/* Search and Expand/Collapse */}
              <div className="space-y-3">
                <div className="relative group">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors" size={18} />
                  <input
                    type="text"
                    placeholder="Search datasets..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border border-transparent focus:border-emerald-500/50 rounded-2xl text-xs font-bold outline-none transition-all"
                  />
                </div>
                {(filteredDatasetsTree.manteo.length > 0 || Object.keys(filteredDatasetsTree.bigquery).length > 0) && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={expandAll}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-gray-500 hover:text-emerald-600 transition-colors"
                    >
                      <ChevronsDown size={14} />
                      Expand All
                    </button>
                    <button
                      onClick={collapseAll}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-gray-500 hover:text-emerald-600 transition-colors"
                    >
                      <ChevronsUp size={14} />
                      Collapse All
                    </button>
                  </div>
                )}
              </div>

              {isLoadingDatasets ? (
                <div className="py-12 flex flex-col items-center justify-center gap-4">
                  <div className="w-8 h-8 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Scanning Available Resources...</span>
                </div>
              ) : (filteredDatasetsTree.manteo.length > 0 || Object.keys(filteredDatasetsTree.bigquery).length > 0) ? (
                <div className="space-y-1">
                  {/* Manteo Warehouse Section */}
                  {filteredDatasetsTree.manteo.length > 0 && (
                    <TreeNode
                      item={{ id: 'manteo', name: 'Manteo Warehouse', type: 'Internal High-Performance Storage' }}
                      level={0}
                      isExpanded={expandedNodes.has('manteo')}
                      onToggle={() => toggleNode('manteo')}
                      icon={Folder}
                      iconColor="text-emerald-500"
                      children={
                        <div className="mt-1">
                          {filteredDatasetsTree.manteo.map(item => {
                            const isSelected = formData.selectedSources.find(s => s.id === item.id);
                            const currentSelectedType = formData.selectedSources.length > 0 ? (
                              formData.selectedSources[0].type === 'bigquery' ? 'bigquery' : 'local'
                            ) : null;
                            const thisSourceType = item.type === 'bigquery' ? 'bigquery' : 'local';
                            const isTypeDisabled = currentSelectedType && currentSelectedType !== thisSourceType;
                            const isStatusDisabled = item.status && item.status !== 'ready';
                            const isDisabled = isTypeDisabled || isStatusDisabled;
                            return (
                              <TreeNode
                                key={item.id}
                                item={{ id: item.id, name: item.name || item.id, type: item.type, status: item.status }}
                                level={1}
                                onSelect={() => !isDisabled && toggleSource(item)}
                                icon={Table}
                                iconColor="text-emerald-500/70"
                                isSelected={!!isSelected}
                                isDisabled={isDisabled}
                              />
                            );
                          })}
                        </div>
                      }
                    />
                  )}

                  {/* BigQuery Section */}
                  {Object.keys(filteredDatasetsTree.bigquery).length > 0 && (
                    <TreeNode
                      item={{ id: 'bigquery', name: 'BigQuery Connection', type: 'Enterprise Data Warehouse' }}
                      level={0}
                      isExpanded={expandedNodes.has('bigquery')}
                      onToggle={() => toggleNode('bigquery')}
                      icon={Server}
                      iconColor="text-blue-500"
                      children={
                        <div className="mt-1">
                          {Object.entries(filteredDatasetsTree.bigquery).map(([projectId, datasets]) => (
                            <TreeNode
                              key={projectId}
                              item={{ id: projectId, name: projectId, type: 'GCP Project' }}
                              level={1}
                              isExpanded={expandedNodes.has(projectId)}
                              onToggle={() => toggleNode(projectId)}
                              icon={Folder}
                              iconColor="text-blue-500"
                              children={
                                <div className="mt-1">
                                  {Object.entries(datasets).map(([datasetId, items]) => (
                                    <TreeNode
                                      key={datasetId}
                                      item={{ id: datasetId, name: datasetId, type: 'Dataset' }}
                                      level={2}
                                      isExpanded={expandedNodes.has(datasetId)}
                                      onToggle={() => toggleNode(datasetId)}
                                      icon={Folder}
                                      iconColor="text-blue-400"
                                      children={
                                        <div className="mt-1">
                                          {items.map(item => {
                                            const isSelected = formData.selectedSources.find(s => s.id === item.id);
                                            const currentSelectedType = formData.selectedSources.length > 0 ? (
                                              formData.selectedSources[0].type === 'bigquery' ? 'bigquery' : 'local'
                                            ) : null;
                                            const thisSourceType = item.type === 'bigquery' ? 'bigquery' : 'local';
                                            const isTypeDisabled = currentSelectedType && currentSelectedType !== thisSourceType;
                                            const isStatusDisabled = item.status && item.status !== 'ready';
                                            const isDisabled = isTypeDisabled || isStatusDisabled;
                                            return (
                                              <TreeNode
                                                key={item.id}
                                                item={{
                                                  id: item.id,
                                                  name: item.tableName || item.name,
                                                  type: 'Table',
                                                  status: item.status
                                                }}
                                                level={3}
                                                onSelect={() => !isDisabled && toggleSource(item)}
                                                icon={Table}
                                                iconColor="text-blue-500/50"
                                                isSelected={!!isSelected}
                                                isDisabled={isDisabled}
                                              />
                                            );
                                          })}
                                        </div>
                                      }
                                    />
                                  ))}
                                </div>
                              }
                            />
                          ))}
                        </div>
                      }
                    />
                  )}
                </div>
              ) : (
                <div className="py-12 text-center border-2 border-dashed border-gray-100 dark:border-gray-800 rounded-3xl">
                  <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">No datasets found</p>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl mb-4">
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  <strong>Optional:</strong> Select custom SQL functions to make available to your AI assistant in this workspace. You can skip this step and add functions later.
                </p>
              </div>

              {/* Search and Expand/Collapse */}
              <div className="space-y-3">
                <div className="relative group">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors" size={18} />
                  <input
                    type="text"
                    placeholder="Search functions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border border-transparent focus:border-emerald-500/50 rounded-2xl text-xs font-bold outline-none transition-all"
                  />
                </div>
                {(Object.keys(filteredFunctionsTree.bigquery).length > 0 || filteredFunctionsTree.custom.length > 0) && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={expandAll}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-gray-500 hover:text-emerald-600 transition-colors"
                    >
                      <ChevronsDown size={14} />
                      Expand All
                    </button>
                    <button
                      onClick={collapseAll}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-gray-500 hover:text-emerald-600 transition-colors"
                    >
                      <ChevronsUp size={14} />
                      Collapse All
                    </button>
                  </div>
                )}
              </div>

              {isLoadingUDFs ? (
                <div className="py-12 flex flex-col items-center justify-center gap-4">
                  <div className="w-8 h-8 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Loading Functions...</span>
                </div>
              ) : (Object.keys(filteredFunctionsTree.bigquery).length > 0 || filteredFunctionsTree.custom.length > 0) ? (
                <div className="space-y-1">
                  {/* BigQuery Functions Section */}
                  {Object.keys(filteredFunctionsTree.bigquery).length > 0 && (
                    <TreeNode
                      item={{ id: 'bigquery', name: 'BigQuery Connection', type: 'Enterprise Data Warehouse' }}
                      level={0}
                      isExpanded={expandedNodes.has('bigquery')}
                      onToggle={() => toggleNode('bigquery')}
                      icon={Server}
                      iconColor="text-blue-500"
                      children={
                        <div className="mt-1">
                          {Object.entries(filteredFunctionsTree.bigquery).map(([projectId, datasets]) => (
                            <TreeNode
                              key={projectId}
                              item={{ id: projectId, name: projectId, type: 'GCP Project' }}
                              level={1}
                              isExpanded={expandedNodes.has(projectId)}
                              onToggle={() => toggleNode(projectId)}
                              icon={Folder}
                              iconColor="text-blue-500"
                              children={
                                <div className="mt-1">
                                  {Object.entries(datasets).map(([datasetId, items]) => (
                                    <TreeNode
                                      key={datasetId}
                                      item={{ id: datasetId, name: datasetId, type: 'Dataset' }}
                                      level={2}
                                      isExpanded={expandedNodes.has(datasetId)}
                                      onToggle={() => toggleNode(datasetId)}
                                      icon={Folder}
                                      iconColor="text-blue-400"
                                      children={
                                        <div className="mt-1">
                                          {items.map(item => {
                                            const isSelected = formData.selectedUDFs.find(u => u.id === item.id);
                                            return (
                                              <TreeNode
                                                key={item.id}
                                                item={{
                                                  id: item.id,
                                                  name: item.functionName || item.name,
                                                  signature: item.signature
                                                }}
                                                level={3}
                                                onSelect={() => toggleUDF(item)}
                                                icon={Terminal}
                                                iconColor="text-blue-500/50"
                                                isSelected={!!isSelected}
                                              />
                                            );
                                          })}
                                        </div>
                                      }
                                    />
                                  ))}
                                </div>
                              }
                            />
                          ))}
                        </div>
                      }
                    />
                  )}

                  {/* Custom Functions Section */}
                  {filteredFunctionsTree.custom.length > 0 && (
                    <TreeNode
                      item={{ id: 'custom', name: 'Custom Functions', type: 'User-Defined Functions' }}
                      level={0}
                      isExpanded={expandedNodes.has('custom')}
                      onToggle={() => toggleNode('custom')}
                      icon={Folder}
                      iconColor="text-emerald-500"
                      children={
                        <div className="mt-1">
                          {filteredFunctionsTree.custom.map(item => {
                            const isSelected = formData.selectedUDFs.find(u => u.id === item.id);
                            return (
                              <TreeNode
                                key={item.id}
                                item={{
                                  id: item.id,
                                  name: item.name,
                                  signature: item.signature
                                }}
                                level={1}
                                onSelect={() => toggleUDF(item)}
                                icon={Terminal}
                                iconColor="text-emerald-500/70"
                                isSelected={!!isSelected}
                              />
                            );
                          })}
                        </div>
                      }
                    />
                  )}
                </div>
              ) : (
                <div className="py-12 text-center border-2 border-dashed border-gray-100 dark:border-gray-800 rounded-3xl">
                  <Code size={32} className="mx-auto text-gray-300 dark:text-gray-700 mb-3" />
                  <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px] mb-2">No functions available</p>
                  <p className="text-gray-400 text-[9px]">Create custom functions or import from BigQuery first</p>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Workspace Name</label>
                  <input
                    autoFocus
                    className="w-full bg-gray-50 dark:bg-gray-900 border-2 border-transparent focus:border-emerald-500 rounded-3xl p-5 text-gray-900 dark:text-white font-bold transition-all outline-none"
                    placeholder="e.g. Sales Analysis Q4"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Description (Optional)</label>
                  <textarea
                    rows={4}
                    className="w-full bg-gray-50 dark:bg-gray-900 border-2 border-transparent focus:border-emerald-500 rounded-3xl p-5 text-gray-900 dark:text-white font-medium text-sm transition-all outline-none resize-none"
                    placeholder="What are you analyzing in this workspace?"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
              </div>

              <div className="p-8 bg-gray-50/50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800 rounded-[40px] space-y-8">
                <div className="flex gap-6">
                  <div className="p-4 bg-white dark:bg-gray-800 rounded-3xl shadow-sm text-emerald-600 shrink-0 h-fit">
                    <Layout size={32} />
                  </div>
                  <div>
                    <h4 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tight">{formData.name || "Untitled Workspace"}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
                      {formData.description || "No description provided."}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/50 dark:bg-gray-800/50 p-4 rounded-3xl border border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar size={14} className="text-gray-400" />
                      <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Initialization</span>
                    </div>
                    <p className="text-xs font-bold text-gray-900 dark:text-gray-100">Today, Jan 10 2026</p>
                  </div>
                  <div className="bg-white/50 dark:bg-gray-800/50 p-4 rounded-3xl border border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-2">
                      <Layers size={14} className="text-gray-400" />
                      <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Connected Sources</span>
                    </div>
                    <p className="text-xs font-bold text-gray-900 dark:text-gray-100">{formData.selectedSources.length} Datasets</p>
                  </div>
                </div>

                {formData.selectedSources.length > 0 && (
                  <div>
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-4 ml-1">Datasets Summary</span>
                    <div className="flex flex-wrap gap-2">
                      {formData.selectedSources.map(s => (
                        <div key={s.id} className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 rounded-xl text-[10px] font-black uppercase tracking-tight">
                          <Check size={10} strokeWidth={4} />
                          {s.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {formData.selectedUDFs.length > 0 && (
                  <div>
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-4 ml-1">Functions Summary</span>
                    <div className="flex flex-wrap gap-2">
                      {formData.selectedUDFs.map(u => (
                        <div key={u.id} className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-xl text-[10px] font-black uppercase tracking-tight">
                          <Code size={10} strokeWidth={4} />
                          {u.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-8 bg-gray-50/50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between shrink-0">
          <button
            onClick={step === 1 ? onClose : handleBack}
            className="px-8 py-4 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all"
          >
            {step === 1 ? "Cancel" : "Go Back"}
          </button>

          {step < 3 ? (
            <button
              onClick={handleNext}
              className="flex items-center gap-3 px-10 py-4 bg-emerald-600 text-white rounded-[24px] hover:bg-emerald-700 transition-all font-black text-[10px] uppercase tracking-[0.2em] shadow-xl shadow-emerald-600/20 disabled:opacity-50 disabled:shadow-none"
            >
              {step === 2 ? (formData.selectedUDFs.length > 0 ? 'Next' : 'Skip') : 'Continue'}
              <ChevronRight size={16} strokeWidth={3} />
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={!formData.name.trim()}
              className="flex items-center gap-3 px-10 py-4 bg-emerald-600 text-white rounded-[24px] hover:bg-emerald-700 transition-all font-black text-[10px] uppercase tracking-[0.2em] shadow-xl shadow-emerald-600/20 animate-pulse disabled:opacity-50 disabled:shadow-none disabled:animate-none"
            >
              <Check size={16} strokeWidth={3} />
              Initialize Workspace
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateWorkspaceWizard;
